import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLocation } from '@/contexts/LocationContext';
import { pointCookData } from '@/data/centres/pointCook';
import { carolineSpringsData } from '@/data/centres/carolineSprings';
import { tarneitData } from '@/data/centres/tarneit';
import { truganinaData } from '@/data/centres/truganina';
import { clydeData } from '@/data/centres/clyde';
import { penrithData } from '@/data/centres/penrith';
import { riverstoneData } from '@/data/centres/riverstone';
import { thePondsData } from '@/data/centres/thePonds';
import { colebeeData } from '@/data/centres/colebee';
import type { CentreData } from '@/data/centres/types';

const LAUNCH_RETIRE_DAYS = 21;

const ALL_CENTRES: CentreData[] = [
  pointCookData,
  carolineSpringsData,
  tarneitData,
  truganinaData,
  clydeData,
  penrithData,
  riverstoneData,
  thePondsData,
  colebeeData,
];

interface Props {
  surface: 'homepage' | 'centres';
  regionLabel: string;
}

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

const formatLong = (d: Date) => `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;

const NewCentreLaunch = ({ surface, regionLabel }: Props) => {
  const { selectedState } = useLocation();

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const candidates = ALL_CENTRES.filter(c => c.state === selectedState && c.launchDate);

  const filtered = candidates.filter(c => {
    const d = new Date(c.launchDate as string);
    const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays <= LAUNCH_RETIRE_DAYS;
  });

  if (filtered.length === 0) return null;

  const uniqueDates = Array.from(new Set(filtered.map(c => c.launchDate)));
  if (uniqueDates.length > 1) return null;

  const launch = new Date(filtered[0].launchDate as string);
  const isOpen = now.getTime() >= launch.getTime();
  const status: 'opening' | 'open' = isOpen ? 'open' : 'opening';

  const stateName = filtered[0].stateName;
  const count = filtered.length;

  const nameList = filtered.map(c => c.name).join(' and ');

  const eyebrow = status === 'opening'
    ? `Now opening · ${formatLong(launch)}`
    : 'Now open';

  const heading = surface === 'homepage'
    ? (count === 1
        ? `A new Schoolola centre in ${regionLabel}`
        : `Two new Schoolola centres in ${regionLabel}`)
    : (count === 1
        ? `A new centre joining our ${stateName} network`
        : `Two new centres joining our ${stateName} network`);

  const lead = status === 'opening'
    ? `${nameList} ${count === 1 ? 'opens' : 'open'} ${formatLong(launch)} - visit ${count === 1 ? 'the page' : 'their pages'} to book an assessment.`
    : `${nameList} ${count === 1 ? 'is' : 'are'} now open - visit ${count === 1 ? 'the page' : 'their pages'} to book an assessment.`;

  // Emblem data
  const day = launch.getDate();
  const monthYear = `${MONTHS[launch.getMonth()].toUpperCase()} ${launch.getFullYear()}`;
  const regionUpper = regionLabel.toUpperCase();

  return (
    <section className="bg-primary/5 border-y border-border/60 py-14 md:py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto grid md:grid-cols-[210px_1fr] gap-8 md:gap-12 items-center">
          {/* Emblem */}
          <div className="flex justify-center md:justify-start">
            <svg viewBox="0 0 210 210" width="210" height="210" role="img" aria-label={`Grand opening ${day} ${MONTHS[launch.getMonth()]} ${launch.getFullYear()}`}>
              <defs>
                <path id="ncl-top" d="M 25 105 A 80 80 0 0 1 185 105" fill="none" />
                <path id="ncl-bottom" d="M 30 110 A 75 75 0 0 0 180 110" fill="none" />
              </defs>
              <circle cx="105" cy="105" r="95" fill="none" stroke="hsl(var(--primary))" strokeWidth="1.5" />
              <circle cx="105" cy="105" r="83" fill="none" stroke="hsl(var(--accent))" strokeWidth="1" />
              <text fill="hsl(var(--primary))" fontSize="11" fontWeight="500" letterSpacing="3">
                <textPath href="#ncl-top" startOffset="50%" textAnchor="middle">GRAND OPENING</textPath>
              </text>
              <text fill="hsl(var(--muted-foreground))" fontSize="9" fontWeight="500" letterSpacing="2.5">
                <textPath href="#ncl-bottom" startOffset="50%" textAnchor="middle">{regionUpper}</textPath>
              </text>
              <text x="105" y="108" textAnchor="middle" fill="hsl(var(--foreground))" fontSize="52" fontWeight="500">{day}</text>
              <text x="105" y="132" textAnchor="middle" fill="hsl(var(--primary))" fontSize="11" fontWeight="500" letterSpacing="2.5">{monthYear}</text>
              <circle cx="105" cy="148" r="2.5" fill="hsl(var(--accent))" />
            </svg>
          </div>

          {/* Content */}
          <div>
            <p className="text-xs font-medium text-primary uppercase tracking-wider mb-3">{eyebrow}</p>
            <h2 className="text-2xl md:text-3xl font-medium text-foreground mb-3">{heading}</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl">{lead}</p>

            <ul className="space-y-2.5">
              {filtered.map(c => (
                <li key={c.slug}>
                  <Link
                    to={`/centres/${c.slug}`}
                    className="flex items-center justify-between gap-4 bg-background border border-border/60 hover:border-primary rounded-lg px-4 py-3 transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="text-foreground font-medium truncate">{c.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {c.suburb}{c.address?.postcode ? ` ${c.address.postcode}` : ''}
                      </div>
                    </div>
                    <span className="flex items-center gap-1.5 text-sm font-medium text-primary flex-shrink-0">
                      Visit page
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewCentreLaunch;
